import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../../utils/constants";
import { clearFavorites, getFavorites } from "../favorites/favoritesSlice";
import { mapGiftItemsForCart } from "../../utils/productBadges";

const buildCartLine = (payload) => {
  const giftItems = mapGiftItemsForCart(payload);
  const { giftProducts, ...rest } = payload;

  return {
    ...rest,
    giftItems: giftItems.length ? giftItems : (rest.giftItems || []),
  };
};

export const createUser = createAsyncThunk(
  "users/createUser",
  async (payload, thunkAPI) => {
    try {
      const formData = new FormData();
      formData.append('name', payload.name);
      formData.append('email', payload.email);
      formData.append('password', payload.password);
      
      if (payload.avatar && payload.avatar instanceof File) {
        formData.append('avatar', payload.avatar);
      } else if (payload.avatar && typeof payload.avatar === 'string') {
        // Если это URL строки, отправляем как есть
        formData.append('avatar', payload.avatar);
      }

      const res = await axios.post(`${BASE_URL}/user/registration`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const userData = {
        id: res.data.id,
        email: res.data.email,
        name: res.data.name,
        avatar: res.data.avatar,
        role: String(res.data.role || '').toUpperCase(),
        access_token: res.data.access_token,
      };

      if (res.data.access_token) {
        localStorage.setItem('token', res.data.access_token);
        await thunkAPI.dispatch(getFavorites());
      }

      return userData;
    } catch (err) {
      console.log(err);
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message || 'Помилка');
    }
  }
);

/** Восстановление сессии з JWT после перезагрузки страницы */
export const initAuth = createAsyncThunk("users/initAuth", async (_, thunkAPI) => {
  const tokenAtStart = localStorage.getItem("token");
  if (!tokenAtStart) {
    thunkAPI.dispatch(clearFavorites());
    return null;
  }
  try {
    const res = await axios.get(`${BASE_URL}/user/auth`, {
      headers: { Authorization: `Bearer ${tokenAtStart}` },
    });
    const tokenNow = localStorage.getItem("token");
    if (tokenNow !== tokenAtStart) {
      return null;
    }
    const userData = {
      id: res.data.id,
      email: res.data.email,
      name: res.data.name,
      avatar: res.data.avatar,
      role: String(res.data.role || '').toUpperCase(),
      access_token: tokenAtStart,
    };

    await thunkAPI.dispatch(getFavorites());

    return userData;
  } catch {
    if (localStorage.getItem("token") === tokenAtStart) {
      localStorage.removeItem("token");
    }
    thunkAPI.dispatch(clearFavorites());
    return null;
  }
});

export const loginUser = createAsyncThunk(
  "users/loginUser",
  async (payload, thunkAPI) => {
    try {
      const { remember, ...credentials } = payload;
      const res = await axios.post(`${BASE_URL}/user/login`, {
        ...credentials,
        remember: Boolean(remember),
      });
      const userData = {
        id: res.data.id,
        email: res.data.email,
        name: res.data.name,
        avatar: res.data.avatar,
        role: String(res.data.role || '').toUpperCase(),
        access_token: res.data.access_token
      };
      
      if (res.data.access_token) {
        localStorage.setItem('token', res.data.access_token);
        await thunkAPI.dispatch(getFavorites());
      }

      return userData;
    } catch (err) {
      console.log(err);
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message || 'Помилка');
    }
  }
);

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async (payload, thunkAPI) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Токен не знайдено');
      }

      const res = await axios.post(`${BASE_URL}/user/profile`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      return res.data;
    } catch (err) {
      console.log(err);
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message || 'Помилка');
    }
  }
);

export const createOrder = createAsyncThunk(
  "users/createOrder",
  async (payload, thunkAPI) => {
    try {
      console.log('Отправка заказа:', payload);
      const res = await axios.post(`${BASE_URL}/orders`, payload);
      return res.data;
    } catch (err) {
      console.error('Помилка при створенні замовлення:', err);
      console.error('Деталі помилки:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message || 'Помилка при створенні замовлення');
    }
  }
);

const addCurrentUser = (state, { payload }) => {
  state.currentUser = payload;
};

const userSlice = createSlice({
  name: "user",
  initialState: {
    currentUser: null,
    cart: [],
    isLoading: false,
    formType: "signup",
    showForm: false,
    checkoutData: null,
    sessionInitialized: false,
  },
  reducers: {
    setCheckoutData: (state, action) => {
      return { ...state, checkoutData: action.payload };
    },

    addItemToCart: (state, { payload }) => {
      const line = buildCartLine(payload);
      let newCart = [...state.cart];
      const found = state.cart.find(({ id }) => id === line.id);

      if (found) {
        newCart = newCart.map((item) => (
          item.id === line.id
            ? {
              ...item,
              ...line,
              quantity: payload.quantity || item.quantity + 1,
              giftItems: line.giftItems.length ? line.giftItems : (item.giftItems || []),
            }
            : item
        ));
      } else {
        newCart.push({ ...line, quantity: payload.quantity || 1 });
      }

      state.cart = newCart;
    },
    removeItemFromCart: (state, { payload }) => {
      state.cart = state.cart.filter(({ id }) => id !== payload);
    },
    clearCart: (state) => {
      state.cart = [];
    },
    toggleForm: (state, { payload }) => {
      state.showForm = payload;
    },
    toggleFormType: (state, { payload }) => {
      state.formType = payload;
    },
    setCurrentUser: (state, { payload }) => {
      state.currentUser = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initAuth.fulfilled, (state, action) => {
      state.sessionInitialized = true;
      if (action.payload) {
        state.currentUser = action.payload;
      }
    });
    builder.addCase(initAuth.rejected, (state) => {
      state.sessionInitialized = true;
    });
    builder.addCase(createUser.fulfilled, (state, action) => {
      addCurrentUser(state, { payload: action.payload });
      state.sessionInitialized = true;
      if (action.payload?.access_token) {
        localStorage.setItem('token', action.payload.access_token);
      }
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      addCurrentUser(state, { payload: action.payload });
      state.sessionInitialized = true;
      if (action.payload?.access_token) {
        localStorage.setItem('token', action.payload.access_token);
      }
    });
    builder.addCase(updateUser.fulfilled, (state, action) => {
      if (action.payload) {
        const token = state.currentUser?.access_token || localStorage.getItem('token');
        state.currentUser = {
          ...action.payload,
          role: String(action.payload.role || '').toUpperCase(),
          access_token: token,
        };
      }
    });
  },
});

export const { addItemToCart, removeItemFromCart, toggleForm, toggleFormType, setCheckoutData, clearCart, setCurrentUser } =
  userSlice.actions;

export default userSlice.reducer;
